/**
 * ============================================================================
 * CORE UTILITY: Authentication & Role Inspection
 * ============================================================================
 * Purpose:
 *   Provides standardized role detection and current user retrieval across
 *   Sidebar, TopBar, and feature pages (SuperAdmin, Admin, Reseller, Account, User).
 * ============================================================================
 */

import { STORAGE_KEYS } from "../config/constants";

/**
 * Retrieves the currently logged-in user object from localStorage.
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.USER || "user");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    console.error("Failed to parse user from localStorage:", e);
    return null;
  }
};

/**
 * Determines normalized role: "superadmin" | "admin" | "reseller" | "account" | "user"
 * @param {Object} [user]
 * @returns {"superadmin" | "admin" | "reseller" | "account" | "user"}
 */
export const getUserRole = (user = getCurrentUser()) => {
  if (!user) return "user";

  const username = (user.str_username || "").trim().toLowerCase();
  const roleId = String(user.str_role_id || user.int_role_id || "");
  const roleName = (
    user.role?.str_role_name ||
    user.str_role_name ||
    user.role_name ||
    ""
  ).trim().toLowerCase();

  // 1. SuperAdmin: role ID 1, username "superadmin", or role name containing "superadmin"
  if (
    username === "superadmin" ||
    roleId === "1" ||
    roleName.includes("superadmin") ||
    roleName.includes("super admin")
  ) {
    return "superadmin";
  }

  // 2. Admin: role ID 13 or 2, role name "admin", or username starting with/containing "admin"
  if (
    roleId === "13" ||
    roleId === "2" ||
    roleName === "admin" ||
    roleName.includes("admin") ||
    username.startsWith("admin_") ||
    username === "admin"
  ) {
    return "admin";
  }

  // 3. Reseller: role ID 14, role name "reseller"
  if (
    roleId === "14" ||
    roleName === "reseller" ||
    roleName.includes("reseller") ||
    username.startsWith("reseller_") ||
    username === "reseller"
  ) {
    return "reseller";
  }

  // 4. Account (Financial / Accounts Manager): role ID 6, role name "account"
  if (
    roleId === "6" ||
    roleName === "account" ||
    roleName.includes("account") ||
    username.startsWith("account_") ||
    username === "account"
  ) {
    return "account";
  }

  // 5. User: tenant / client user
  return "user";
};

export const isSuperAdmin = (user = getCurrentUser()) => getUserRole(user) === "superadmin";

export const isAdmin = (user = getCurrentUser()) => {
  const role = getUserRole(user);
  return role === "admin" || role === "superadmin";
};

export const isReseller = (user = getCurrentUser()) => getUserRole(user) === "reseller";

export const isAccountRole = (user = getCurrentUser()) => getUserRole(user) === "account";

export const isRegularUser = (user = getCurrentUser()) => getUserRole(user) === "user";

export const isAgent = (user = getCurrentUser()) => {
  if (!user) return false;
  const roleId = String(user.str_role_id || user.int_role_id || "");
  const roleName = (user.str_role_name || user.role_name || "").toLowerCase();
  return roleId === "8" || roleName === "agent";
};

export const canDeleteCampaign = (user = getCurrentUser()) => isAdmin(user);

/**
 * Checks if the current user has a specific granular permission key.
 *
 * @param {Object} [user] - User object (defaults to current user)
 * @param {string} [permissionKey] - Permission identifier, e.g. "TINY_URL_MANAGEMENT"
 * @returns {boolean}
 */
export const hasPermission = (user = getCurrentUser(), permissionKey = "TINY_URL_MANAGEMENT") => {
  if (!user) return false;

  // SuperAdmin and Admin always possess full operational authority
  if (isSuperAdmin(user) || isAdmin(user)) return true;

  // Feature-specific explicit disable checks
  if (permissionKey === "TINY_URL_MANAGEMENT") {
    if (user.bool_tiny_url_enabled === false || user.has_tiny_url_access === false) {
      return false;
    }
  }

  if (permissionKey === "INDIAMART_INTEGRATION") {
    if (user.bool_indiamart_enabled === false || user.has_indiamart_access === false) {
      return false;
    }
  }

  if (permissionKey === "CHAT_MANAGEMENT") {
    if (user.bool_chat_enabled === false || user.has_chat_access === false) {
      return false;
    }
  }

  // Granular permissions array attached to user session
  if (Array.isArray(user.permissions)) {
    if (user.permissions.includes(permissionKey)) return true;
    if (user.permissions.includes("MANAGE_TINY_URLS") && permissionKey === "TINY_URL_MANAGEMENT") return true;
    if (user.permissions.includes("INDIAMART") && permissionKey === "INDIAMART_INTEGRATION") return true;
    if (user.permissions.includes("LIVE_CHAT") && permissionKey === "CHAT_MANAGEMENT") return true;
  }

  // Granular operations array attached to user session
  if (Array.isArray(user.operations)) {
    const hasOp = user.operations.some(
      (op) =>
        op === permissionKey ||
        op.str_operation_key === permissionKey ||
        op.key === permissionKey ||
        (permissionKey === "TINY_URL_MANAGEMENT" && op.str_operation_name?.toLowerCase().includes("tiny url")) ||
        (permissionKey === "INDIAMART_INTEGRATION" && op.str_operation_name?.toLowerCase().includes("indiamart")) ||
        (permissionKey === "CHAT_MANAGEMENT" && op.str_operation_name?.toLowerCase().includes("chat"))
    );
    if (hasOp) return true;
  }

  // Default enabled for workspace roles
  return true;
};

export const canManageTinyUrls = (user = getCurrentUser()) => hasPermission(user, "TINY_URL_MANAGEMENT");
export const canAccessIndiaMart = (user = getCurrentUser()) => hasPermission(user, "INDIAMART_INTEGRATION");
export const canAccessChat = (user = getCurrentUser()) => hasPermission(user, "CHAT_MANAGEMENT");


